import "../../styles/resume.css";
import { BaseSection, BaseSectionProps } from "@/components/sections/BaseSection";
import { Trans, useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiChevronDown, HiChevronUp, HiArrowUpRight, HiCubeTransparent } from "react-icons/hi2";
import { FiArrowUpRight, FiX } from "react-icons/fi";
import {
    SiAmazon, SiChainlink, SiClaude, SiEthereum, SiLangchain, SiMysql, SiNestjs, SiNextdotjs,
    SiNodedotjs, SiOpenai, SiPostgresql, SiReact, SiRust, SiSolidity, SiTypescript
} from "react-icons/si";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useScrollLock } from "@/hooks/useScrollLock";
import type { TechStackItem } from "@/types/tech-stack";
import { scrollToProject, type PortfolioProjectId } from "@/utils/portfolio-highlight";

// Reveal variants for the row / list-item level animation.
const rowVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.16, 1, 0.3, 1]
        }
    }
};

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.1
        }
    }
};

// Reduced motion variants for accessibility
const reducedRowVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            duration: 0.3
        }
    }
};

const reducedContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.05,
            delayChildren: 0.05
        }
    }
};

type ExperienceItem = {
    dateKey: string;
    titleKey: string;
    companyKey: string;
    highlightKeys: string[];
    descriptionKeys: string[];
    techStack: TechStackItem[];
    portfolioProjectId?: PortfolioProjectId;
};

const INITIAL_VISIBLE_EXPERIENCES = 3;

const ANTHROPIC_CERTIFICATIONS = [
    { nameKey: "resume.anthropicClaudeCodeInAction", verifyKey: "resume.anthropicClaudeCodeInActionVerify" },
    { nameKey: "resume.anthropicAiFluency", verifyKey: "resume.anthropicAiFluencyVerify" },
    { nameKey: "resume.anthropicMcpAdvanced", verifyKey: "resume.anthropicMcpAdvancedVerify" },
    { nameKey: "resume.anthropicAiFluencyBadge", verifyKey: "resume.anthropicAiFluencyBadgeVerify" },
    { nameKey: "resume.anthropicAiCapabilitiesLimitations", verifyKey: "resume.anthropicAiCapabilitiesLimitationsVerify" }
];

// Experience data - ordered from most recent to oldest
const experiences: ExperienceItem[] = [
    {
        dateKey: "resume.foundingEngineerDate",
        titleKey: "resume.foundingEngineer",
        companyKey: "resume.omen",
        highlightKeys: [
            "resume.foundingEngineerHighlight1",
            "resume.foundingEngineerHighlight2",
            "resume.foundingEngineerHighlight3"
        ],
        descriptionKeys: [
            "resume.foundingEngineerDescription1",
            "resume.foundingEngineerDescription2",
            "resume.foundingEngineerDescription3",
            "resume.foundingEngineerDescription4",
            "resume.foundingEngineerDescription5"
        ],
        techStack: [
            { icon: SiReact, name: "React" },
            { icon: SiNestjs, name: "Nest.js" },
            { icon: SiPostgresql, name: "PostgreSQL" },
            { icon: SiSolidity, name: "Solidity" },
            { icon: SiChainlink, name: "Chainlink" },
            { icon: SiAmazon, name: "AWS" },
            { icon: SiClaude, name: "Claude Code" }
        ],
        portfolioProjectId: "omen"
    },
    {
        dateKey: "resume.aiEngineerDate",
        titleKey: "resume.aiEngineer",
        companyKey: "resume.animocaBrands",
        highlightKeys: [
            "resume.aiEngineerHighlight1",
            "resume.aiEngineerHighlight2",
            "resume.aiEngineerHighlight3"
        ],
        descriptionKeys: [
            "resume.aiEngineerDescription1",
            "resume.aiEngineerDescription2",
            "resume.aiEngineerDescription3",
            "resume.aiEngineerDescription4"
        ],
        techStack: [
            { icon: SiNextdotjs, name: "Next.js" },
            { icon: SiNestjs, name: "Nest.js" },
            { icon: SiMysql, name: "MySQL" },
            { icon: SiSolidity, name: "Solidity" },
            { icon: SiLangchain, name: "LangChain" }
        ],
        portfolioProjectId: "aiveronica"
    },
    {
        dateKey: "resume.juniorBlockchainEngineerDate",
        titleKey: "resume.juniorBlockchainEngineer",
        companyKey: "resume.lexerMarkets",
        highlightKeys: [
            "resume.juniorBlockchainEngineerHighlight1",
            "resume.juniorBlockchainEngineerHighlight2",
            "resume.juniorBlockchainEngineerHighlight3"
        ],
        descriptionKeys: [
            "resume.juniorBlockchainEngineerDescription1",
            "resume.juniorBlockchainEngineerDescription2",
            "resume.juniorBlockchainEngineerDescription3"
        ],
        techStack: [
            { icon: SiSolidity, name: "Solidity" },
            { icon: SiEthereum, name: "ethers.js" },
            { icon: SiTypescript, name: "TypeScript" },
            { icon: SiNodedotjs, name: "Node.js" },
            { icon: SiRust, name: "Rust" },
            // react-icons has no Aptos brand mark
            { icon: HiCubeTransparent, name: "Aptos" }
        ]
    },
    {
        dateKey: "resume.juniorBackendDeveloperDate",
        titleKey: "resume.juniorBackendDeveloper",
        companyKey: "resume.bullBLimited",
        highlightKeys: [
            "resume.juniorBackendDeveloperHighlight1",
            "resume.juniorBackendDeveloperHighlight2"
        ],
        descriptionKeys: [
            "resume.juniorBackendDeveloperDescription1",
            "resume.juniorBackendDeveloperDescription2"
        ],
        techStack: [
            { icon: SiReact, name: "React" },
            { icon: SiNextdotjs, name: "Next.js" },
            { icon: SiTypescript, name: "TypeScript" },
            { icon: SiNodedotjs, name: "Node.js" },
            { icon: SiOpenai, name: "OpenAI API" }
        ]
    }
];

export type ResumeProps = BaseSectionProps & {
    onFocusProject?: (projectId: PortfolioProjectId) => void;
};

export const Resume = (props: ResumeProps) => {
    const { t } = useTranslation();
    const { onFocusProject } = props;
    const [isExpanded, setIsExpanded] = useState(false);
    const [openExperienceKey, setOpenExperienceKey] = useState<string | null>(null);

    // Extract inView state for cleaner code
    const isInView = props.section.viewHook.inView;

    const prefersReducedMotion = usePrefersReducedMotion();

    // Select appropriate variants based on user preference
    const container = prefersReducedMotion ? reducedContainerVariants : containerVariants;
    const row = prefersReducedMotion ? reducedRowVariants : rowVariants;

    const visibleExperiences = isExpanded ? experiences : experiences.slice(0, INITIAL_VISIBLE_EXPERIENCES);
    const hasMoreExperiences = experiences.length > INITIAL_VISIBLE_EXPERIENCES;

    const openExperience = (titleKey: string) => setOpenExperienceKey(titleKey);

    return (
        <BaseSection {...props}>
            <div className="row">
                <div className="col-12 col-md-10 col-xl-8">
                    <h6 className="title-heading mb-3" data-backdrop-text={t("resume.title")}>{t("resume.title")}</h6>
                    <h2 className="section-subtitle">{t("resume.subtitle")}</h2>
                    <p className="section-lede">{t("resume.description")}</p>
                </div>
            </div>

            {/* Experience timeline */}
            <motion.div
                className="resume-timeline"
                variants={container}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
            >
                <AnimatePresence mode="sync">
                    {visibleExperiences.map((experience, index) => (
                        <motion.div
                            key={experience.titleKey}
                            className="resume-row"
                            role="button"
                            tabIndex={0}
                            aria-haspopup="dialog"
                            onClick={() => openExperience(experience.titleKey)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    openExperience(experience.titleKey);
                                }
                            }}
                            variants={row}
                            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
                            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
                            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -16 }}
                            transition={{
                                duration: prefersReducedMotion ? 0.2 : 0.5,
                                delay: prefersReducedMotion ? 0 : index * 0.06,
                                ease: [0.16, 1, 0.3, 1]
                            }}
                        >
                            <div className="resume-row-meta">
                                <span className="resume-row-date">{t(experience.dateKey)}</span>
                                <span className="resume-row-company">{t(experience.companyKey)}</span>
                            </div>

                            <div className="resume-row-content">
                                <div className="resume-row-heading">
                                    <h3 className="resume-row-title">{t(experience.titleKey)}</h3>
                                    <span className="resume-row-arrow" aria-hidden="true">
                                        <HiArrowUpRight />
                                    </span>
                                </div>

                                <ul className="resume-row-summary">
                                    {experience.highlightKeys.map((highlightKey) => (
                                        <li key={highlightKey}>{t(highlightKey)}</li>
                                    ))}
                                </ul>

                                {experience.techStack.length > 0 && (
                                    <div className="resume-row-tags">
                                        {experience.techStack.map((tech) => (
                                            <span key={tech.name} className="resume-tag">{tech.name}</span>
                                        ))}
                                    </div>
                                )}

                                <span className="resume-row-cta">{t("resume.viewDetails")}</span>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </motion.div>

            {hasMoreExperiences && (
                <motion.div
                    className="resume-more"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isInView ? 1 : 0 }}
                    transition={{ delay: 0.5 }}
                >
                    <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="button"
                    >
                        <span>
                            {isExpanded
                                ? t('resume.showLess', 'Show Less')
                                : t('resume.viewMore', { count: experiences.length - INITIAL_VISIBLE_EXPERIENCES })}
                        </span>
                        {isExpanded ? (
                            <HiChevronUp className="size-5" />
                        ) : (
                            <HiChevronDown className="size-5" />
                        )}
                    </button>
                </motion.div>
            )}

            {/* Education & certifications */}
            <motion.div
                className="resume-education"
                variants={container}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
            >
                <motion.div className="resume-list-row" variants={row}>
                    <span className="resume-list-date">{t("resume.anthropicAcademyDate")}</span>
                    <h4 className="resume-list-title">{t("resume.anthropicAcademyTitle")}</h4>
                    <span className="resume-list-subtitle">{t("resume.anthropicAcademy")}</span>
                    <ul className="resume-cert-list">
                        {ANTHROPIC_CERTIFICATIONS.map((certification) => (
                            <li key={certification.nameKey}>
                                <a
                                    href={t(certification.verifyKey)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="resume-cert-link"
                                >
                                    {t(certification.nameKey)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </motion.div>

                <motion.div className="resume-list-row" variants={row}>
                    <span className="resume-list-date">{t("resume.googleAiEssentialsCertificateDate")}</span>
                    <h4 className="resume-list-title">{t("resume.googleAiEssentialsCertificate")}</h4>
                    <span className="resume-list-subtitle">{t("resume.googleCoursera")}</span>
                    <p className="resume-list-description">
                        {t("resume.googleAiEssentialsCertificateDescription")}
                    </p>
                </motion.div>

                <motion.div className="resume-list-row" variants={row}>
                    <span className="resume-list-date">{t("resume.bachelorOfComputerEngineeringDate")}</span>
                    <h4 className="resume-list-title">{t("resume.bachelorOfComputerEngineering")}</h4>
                    <span className="resume-list-subtitle">{t("resume.hku")}</span>
                    <p className="resume-list-description">
                        {t("resume.bachelorOfComputerEngineeringGPA")}
                    </p>
                    <p className="resume-list-description">
                        {t("resume.bachelorOfComputerEngineeringMajorGPA")}
                    </p>
                </motion.div>
            </motion.div>

            {experiences.map((experience) => (
                <ExperienceDialog
                    key={experience.titleKey}
                    experience={experience}
                    isOpen={openExperienceKey === experience.titleKey}
                    onClose={() => setOpenExperienceKey(null)}
                    onFocusProject={onFocusProject}
                />
            ))}
        </BaseSection>
    );
};

type ExperienceDialogProps = {
    experience: ExperienceItem;
    isOpen: boolean;
    onClose: () => void;
    onFocusProject?: (projectId: PortfolioProjectId) => void;
};

function ExperienceDialog({ experience, isOpen, onClose, onFocusProject }: ExperienceDialogProps) {
    const { t } = useTranslation();
    const prefersReducedMotion = usePrefersReducedMotion();
    const titleId = useId();
    const panelRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const shouldRestoreFocusRef = useRef(true);

    useScrollLock(isOpen);

    // Move focus into the dialog on open, and back to the trigger on close unless
    // we are deliberately sending the user to the portfolio card instead.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        shouldRestoreFocusRef.current = true;
        previouslyFocusedRef.current =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;

        const focusTimeoutId = window.setTimeout(() => closeButtonRef.current?.focus(), 0);

        return () => {
            window.clearTimeout(focusTimeoutId);
            const previouslyFocused = previouslyFocusedRef.current;
            if (shouldRestoreFocusRef.current && previouslyFocused?.isConnected) {
                previouslyFocused.focus();
            }
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                onClose();
                return;
            }

            if (event.key !== "Tab") {
                return;
            }

            const panel = panelRef.current;
            if (!panel) {
                return;
            }

            const focusable = panel.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            if (focusable.length === 0) {
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;

            if (event.shiftKey && (active === first || !panel.contains(active))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    const handleFocusProject = () => {
        const projectId = experience.portfolioProjectId;
        if (!projectId) {
            return;
        }

        // Skip focus restore so it does not yank the viewport back to the resume.
        shouldRestoreFocusRef.current = false;
        onClose();

        // Wait for the scroll lock to be released before scrolling, then highlight
        // only once the card has actually arrived on screen.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                scrollToProject(projectId, () => onFocusProject?.(projectId));
            });
        });
    };

    const panelEase = [0.76, 0, 0.24, 1] as const;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: prefersReducedMotion ? 0.15 : 0.4 }}
                        onClick={onClose}
                        aria-hidden="true"
                        className="panel-backdrop"
                    />

                    <motion.div
                        ref={panelRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        initial={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
                        animate={prefersReducedMotion ? { opacity: 1 } : { x: 0 }}
                        exit={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
                        transition={{ duration: prefersReducedMotion ? 0.15 : 0.5, ease: panelEase }}
                        className="panel"
                    >
                        <div className="panel-header">
                            <p className="panel-meta">
                                <span>{t(experience.companyKey)}</span>
                                <span>{t(experience.dateKey)}</span>
                            </p>
                            <h2 id={titleId} className="panel-title">
                                {t(experience.titleKey)}
                            </h2>
                            <button
                                type="button"
                                ref={closeButtonRef}
                                onClick={onClose}
                                className="panel-close"
                                aria-label={t("resume.closeDialog")}
                            >
                                <FiX aria-hidden="true" />
                            </button>
                        </div>

                        <div
                            className="panel-body"
                            tabIndex={0}
                            role="group"
                            aria-label={t(experience.titleKey)}
                        >
                            <div className="panel-block">
                                <ul className="panel-list">
                                    {experience.descriptionKeys
                                        .filter((key) => t(key) !== key && t(key).length > 0)
                                        .map((descriptionKey) => (
                                            <li key={descriptionKey}>
                                                <Trans i18nKey={descriptionKey} components={{ b: <strong /> }} />
                                            </li>
                                        ))}
                                </ul>
                            </div>

                            {experience.techStack.length > 0 && (
                                <div className="panel-block">
                                    <h3 className="panel-label">
                                        {t("resume.techStackTitle")}
                                    </h3>
                                    <ul className="panel-chips">
                                        {experience.techStack.map((tech) => {
                                            const Icon = tech.icon;
                                            return (
                                                <li key={tech.name}>
                                                    <Icon aria-hidden="true" />
                                                    {tech.name}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {experience.portfolioProjectId && (
                            <div className="panel-footer">
                                <button
                                    type="button"
                                    onClick={handleFocusProject}
                                    className="button button-dark"
                                >
                                    <span>{t("resume.seeProject")}</span>
                                    <FiArrowUpRight aria-hidden="true" />
                                </button>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
}
